import { canonicalCourseType } from './courseTypes';
import { sortCourseTypesForCalendar } from './courseTypeColors';

function normalizeCourseKey(name) {
  return String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function parseAssignedCourses(raw) {
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((c) => String(c).trim()).filter(Boolean);
        }
      } catch {
        /* fall through */
      }
    }
    return trimmed
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
  }
  return [];
}

export function createEmptyCalendarFilters() {
  return {
    selectedTypes: new Set(),
    selectedCourses: new Set(),
  };
}

export function hasActiveCalendarFilters(filters) {
  return (
    (filters?.selectedTypes?.size ?? 0) > 0 || (filters?.selectedCourses?.size ?? 0) > 0
  );
}

function typeMatchesSelection(typeRaw, selectedTypes) {
  if (!selectedTypes?.size) return true;
  const canonical = canonicalCourseType(typeRaw);
  if (!canonical) return false;
  return selectedTypes.has(canonical.toLowerCase());
}

function courseMatchesSelection(courseRaw, selectedCourses) {
  if (!selectedCourses?.size) return true;
  const key = normalizeCourseKey(courseRaw);
  if (!key) return false;
  for (const selected of selectedCourses) {
    if (normalizeCourseKey(selected) === key) return true;
  }
  return false;
}

export function dayMatchesCalendarFilters(
  dateKey,
  { sessionCountsByDate, courseTypesByDate, coursesByDate, filters }
) {
  const sessionCount = sessionCountsByDate?.[dateKey] || 0;
  if (sessionCount <= 0) return false;

  const types = courseTypesByDate?.[dateKey] || [];
  const courses = coursesByDate?.[dateKey] || [];
  const { selectedTypes, selectedCourses } = filters || createEmptyCalendarFilters();

  const typeFilterActive = selectedTypes.size > 0;
  const courseFilterActive = selectedCourses.size > 0;
  if (!typeFilterActive && !courseFilterActive) return true;

  const typeOk =
    !typeFilterActive || types.some((type) => typeMatchesSelection(type, selectedTypes));
  const courseOk =
    !courseFilterActive || courses.some((course) => courseMatchesSelection(course, selectedCourses));

  return typeOk && courseOk;
}

export function getFilteredDayCourseTypes(dateKey, { courseTypesByDate, coursesByDate, filters }) {
  const types = courseTypesByDate?.[dateKey] || [];
  const courses = coursesByDate?.[dateKey] || [];
  const { selectedTypes, selectedCourses } = filters || createEmptyCalendarFilters();

  let filtered = [...types];

  if (selectedTypes.size > 0) {
    filtered = filtered.filter((type) => typeMatchesSelection(type, selectedTypes));
  }

  if (selectedCourses.size > 0 && courses.length > 0) {
    const courseKeys = new Set(
      courses
        .filter((course) => courseMatchesSelection(course, selectedCourses))
        .map((course) => normalizeCourseKey(course))
    );
    if (courseKeys.size === 0) {
      return [];
    }
    if (filtered.length === 0 && selectedTypes.size === 0) {
      return sortCourseTypesForCalendar(types);
    }
  }

  return sortCourseTypesForCalendar(filtered);
}

export function applyCalendarFilters({
  sessionCountsByDate = {},
  courseTypesByDate = {},
  coursesByDate = {},
  filters,
}) {
  const nextSessionCounts = {};
  const nextCourseTypes = {};
  const nextCourses = {};

  const dateKeys = new Set([
    ...Object.keys(sessionCountsByDate),
    ...Object.keys(courseTypesByDate),
    ...Object.keys(coursesByDate),
  ]);

  dateKeys.forEach((dateKey) => {
    if (
      !dayMatchesCalendarFilters(dateKey, {
        sessionCountsByDate,
        courseTypesByDate,
        coursesByDate,
        filters,
      })
    ) {
      return;
    }
    nextSessionCounts[dateKey] = sessionCountsByDate[dateKey] || 0;
    nextCourseTypes[dateKey] = getFilteredDayCourseTypes(dateKey, {
      courseTypesByDate,
      coursesByDate,
      filters,
    });
    nextCourses[dateKey] = (coursesByDate[dateKey] || []).filter((course) =>
      courseMatchesSelection(course, filters?.selectedCourses)
    );
  });

  return {
    sessionCountsByDate: nextSessionCounts,
    courseTypesByDate: nextCourseTypes,
    coursesByDate: nextCourses,
  };
}
